import type { DemoTemplate } from './types';

const JESSICA = 'jessica@pique.so';
const JAY = 'jay@pique.so';
const KATE = 'kate@pique.so';
const MIKE = 'mike@pique.so';
const SARAH = 'sarah@pique.so';
const DAVID = 'david@pique.so';
const RACHEL = 'rachel@pique.so';

export const corporateTemplate: DemoTemplate = {
  id: 'corporate',
  name: 'Corporate',
  description: 'Professional workplace meetings and team collaboration',
  selfAttendeeEmail: JESSICA,

  attendees: [
    {
      email: JESSICA,
      name: 'Jessica Chen',
      avatar: 'https://mockmind-api.uifaces.co/content/human/212.jpg',
    },
    {
      email: JAY,
      name: 'Jay Patel',
      avatar: 'https://mockmind-api.uifaces.co/content/human/172.jpg',
    },
    {
      email: KATE,
      name: 'Kate Morrison',
      avatar: 'https://mockmind-api.uifaces.co/content/human/108.jpg',
    },
    {
      email: MIKE,
      name: 'Mike Rivera',
      avatar: 'https://mockmind-api.uifaces.co/content/human/142.jpg',
    },
    {
      email: SARAH,
      name: 'Sarah Lopez',
      avatar: 'https://mockmind-api.uifaces.co/content/human/156.jpg',
    },
    {
      email: DAVID,
      name: 'David Kim',
      avatar: 'https://mockmind-api.uifaces.co/content/human/136.jpg',
    },
    {
      email: RACHEL,
      name: 'Rachel Green',
      avatar: 'https://mockmind-api.uifaces.co/content/human/189.jpg',
    },
  ],

  previousNotes: [
    {
      id: 'demo-prev-marketing-sync',
      title: 'Marketing Sync',
      forEventId: 'demo-marketing-sync',
      daysAgo: 7,
      attendeeEmails: [JAY],
      overview: 'Reviewed Q4 campaign performance and discussed Q1 budget allocation strategy.',
      briefSummary: 'Q4 leads up 23%. Jay proposed shifting 30% of display budget to LinkedIn (5x vs 2x ROI). Q1 budget allocation and content calendar decisions pending.',
      actionItems: [
        { title: 'Draft Q1 budget reallocation proposal', assignedToEmail: JAY, priority: 'High', daysOverdue: 2 },
        { title: 'Build content calendar for product launch', assignedToEmail: JESSICA, priority: 'High' },
        { title: 'Research influencer partnership opportunities', assignedToEmail: JAY },
        { title: 'Update LinkedIn company page branding', assignedToEmail: JESSICA, priority: 'Medium' },
        { title: 'Pull Q4 analytics report for board deck', assignedToEmail: JAY, priority: 'High', daysOverdue: 1 },
      ],
      transcript: [
        { speakerEmail: JESSICA, timestamp: '00:01', text: "Hey Jay, thanks for joining. Before we dive in, how was your weekend? Did you get any training in for the Austin marathon?" },
        { speakerEmail: JAY, timestamp: '00:08', text: "Yeah, I did a 15-mile run on Saturday. February is coming up fast so I'm really ramping up. My daughter cheered me on at the finish line which was sweet." },
        { speakerEmail: JESSICA, timestamp: '00:18', text: "That's adorable! How's she doing in first grade by the way?" },
        { speakerEmail: JAY, timestamp: '00:22', text: "She's loving it, already reading chapter books. Anyway, let's talk Q4 numbers. I pulled the campaign performance metrics." },
        { speakerEmail: JESSICA, timestamp: '00:30', text: "Perfect. We saw a 23% increase in qualified leads compared to Q3, which is really encouraging." },
        { speakerEmail: JAY, timestamp: '00:42', text: "The LinkedIn campaigns are performing exceptionally well. I think we should double down there for Q1." },
        { speakerEmail: JESSICA, timestamp: '00:50', text: "Agreed. I've been thinking about expanding our thought leadership program on LinkedIn." },
        { speakerEmail: JAY, timestamp: '01:02', text: "On the paid side, I want to flag that we need to optimize ad spend allocation. Display is giving us about 2x return while LinkedIn is closer to 5x." },
        { speakerEmail: JESSICA, timestamp: '01:15', text: "Let's discuss that in our Q1 budget planning. I also want to talk about the content calendar for the product launch." },
      ],
      socialFeedItems: [
        { attendeeEmail: JAY, text: 'Training for the Austin marathon in February', confidence: 0.91, category: 'Fitness', date: 'Dec 20' },
        { attendeeEmail: JAY, text: 'Daughter just started first grade', confidence: 0.85, category: 'Family', date: 'Dec 10' },
      ],
    },
    {
      id: 'demo-prev-erg-summit',
      title: 'Neurodiversity ERG Summit',
      forEventId: 'demo-neurodiversity-erg',
      daysAgo: 14,
      attendeeEmails: [KATE, JAY],
      overview: 'Finalized accommodations guide rollout and reviewed inclusive hiring recommendations.',
      briefSummary: 'Finalized accommodations guide rollout. Kate shared inclusive hiring recommendations from an external consultant. Mentorship matching and budget request still open.',
      actionItems: [
        { title: 'Submit budget request for professional dev workshops', assignedToEmail: KATE, priority: 'High', daysOverdue: 5 },
        { title: 'Finalize mentorship program matching', assignedToEmail: JESSICA, priority: 'Medium', daysOverdue: 3 },
        { title: 'Draft awareness month event plan', assignedToEmail: KATE },
        { title: 'Send accommodations guide to all managers', assignedToEmail: JESSICA, priority: 'High', daysOverdue: 1 },
      ],
      transcript: [
        { speakerEmail: KATE, timestamp: '00:01', text: "Thanks everyone for making time. Jessica, before we start, I heard you went hiking in Colorado recently?" },
        { speakerEmail: JESSICA, timestamp: '00:08', text: "Yes! Just got back. Did a few trails in Rocky Mountain National Park. I've also been trying pottery on weekends." },
        { speakerEmail: KATE, timestamp: '00:20', text: "That sounds lovely. I've been planning a trip to Japan for spring. Jay, how are things with you?" },
        { speakerEmail: JAY, timestamp: '00:28', text: "Good! Busy with marathon training but excited about what we're building here with the ERG." },
        { speakerEmail: KATE, timestamp: '00:35', text: "Let's dive in. The main item today is finalizing the accommodations guide rollout timeline." },
        { speakerEmail: JESSICA, timestamp: '00:45', text: "I've gotten positive feedback from the recent awareness workshop. People found it really valuable." },
        { speakerEmail: KATE, timestamp: '00:55', text: "I also met with the external consultant about inclusive hiring practices. She had excellent recommendations." },
        { speakerEmail: JAY, timestamp: '01:08', text: "Can you share those insights? I'd like to incorporate them into our recruitment process." },
        { speakerEmail: KATE, timestamp: '01:15', text: "Key points: structured interviews, skills-based assessments, and clear job descriptions without unnecessary requirements." },
      ],
      socialFeedItems: [
        { attendeeEmail: JESSICA, text: 'Just got back from a hiking trip in Colorado', confidence: 0.92, category: 'Travel', date: 'Dec 18' },
        { attendeeEmail: JESSICA, text: 'Started learning pottery on weekends', confidence: 0.88, category: 'Hobbies', date: 'Dec 15' },
        { attendeeEmail: KATE, text: 'Planning a trip to Japan in the spring', confidence: 0.87, category: 'Travel', date: 'Dec 12' },
      ],
    },
    {
      id: 'demo-prev-product-roadmap',
      title: 'Q1 Product Roadmap Review',
      forEventId: 'demo-product-roadmap',
      daysAgo: 14,
      attendeeEmails: [KATE, JAY, MIKE],
      overview: 'API v2 migration on track for March. Mobile vs integrations decision deferred pending customer interviews.',
      briefSummary: 'API v2 migration confirmed on track for March. Mobile vs. integrations decision deferred pending 10 customer interviews. Tech debt allocation (20% Q1 time) not yet approved.',
      actionItems: [
        { title: 'Schedule 10 customer interviews', assignedToEmail: KATE, priority: 'High', daysOverdue: 4 },
        { title: 'Finalize API v2 migration timeline', assignedToEmail: MIKE, priority: 'High' },
        { title: 'Propose tech debt allocation plan', assignedToEmail: JAY, priority: 'Medium', daysOverdue: 2 },
        { title: 'Create mobile vs integrations decision doc', assignedToEmail: KATE, priority: 'High' },
        { title: 'Audit API v2 breaking changes for partners', assignedToEmail: MIKE, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: JAY, timestamp: '00:01', text: "Hey Kate, Mike. Ready to review the roadmap?" },
        { speakerEmail: KATE, timestamp: '00:08', text: "Let's focus on Q1 priorities. Mike, where are we on API v2?" },
        { speakerEmail: MIKE, timestamp: '00:15', text: "Engineering confirmed the API v2 migration is on track for March release. We've got the core endpoints done." },
        { speakerEmail: KATE, timestamp: '00:25', text: "Excellent. What about mobile app enhancements versus the new integrations?" },
        { speakerEmail: JAY, timestamp: '00:35', text: "Mobile usage is up 40% quarter over quarter. But we're also getting enterprise integration requests." },
        { speakerEmail: MIKE, timestamp: '00:45', text: "I'd recommend we do the customer interviews first. We need data to make this call." },
        { speakerEmail: KATE, timestamp: '00:55', text: "Agreed. Let's plan at least 10 customer interviews this month." },
        { speakerEmail: JAY, timestamp: '01:05', text: "We should also discuss technical debt. I've identified about 3 weeks of cleanup work." },
        { speakerEmail: MIKE, timestamp: '01:15', text: "I'd support allocating 20% of Q1 engineering time to it. The auth module is getting fragile." },
      ],
      socialFeedItems: [
        { attendeeEmail: KATE, text: 'Finished reading "The Art of Gathering"', confidence: 0.89, category: 'Books', date: 'Dec 19' },
        { attendeeEmail: JAY, text: 'Training for the Austin marathon in February', confidence: 0.91, category: 'Fitness', date: 'Dec 20' },
      ],
    },
    {
      id: 'demo-prev-eng-standup',
      title: 'Engineering Weekly Standup',
      forEventId: 'demo-eng-standup',
      daysAgo: 3,
      attendeeEmails: [MIKE, JAY, SARAH],
      overview: 'Reviewed sprint progress on API migration and design system updates.',
      briefSummary: 'API v2 auth endpoints complete. Design system component library 70% done. Performance regression in search needs investigation.',
      actionItems: [
        { title: 'Investigate search performance regression', assignedToEmail: MIKE, priority: 'High' },
        { title: 'Complete design system component library', assignedToEmail: SARAH, priority: 'Medium' },
        { title: 'Write API v2 migration guide for partners', assignedToEmail: JAY },
        { title: 'Set up automated performance monitoring', assignedToEmail: MIKE, priority: 'Medium' },
        { title: 'Review button and input component PR', assignedToEmail: JAY, priority: 'Low' },
      ],
      transcript: [
        { speakerEmail: MIKE, timestamp: '00:01', text: "Morning team. Quick updates. I finished the auth endpoints for API v2 yesterday." },
        { speakerEmail: JAY, timestamp: '00:08', text: "Nice work. The partner team is asking for a migration guide. I can draft that this week." },
        { speakerEmail: SARAH, timestamp: '00:15', text: "Design system component library is about 70% done. The button and input components are ready for review." },
        { speakerEmail: MIKE, timestamp: '00:25', text: "I noticed a performance regression in search. Loading times went from 200ms to 800ms after the last deploy." },
        { speakerEmail: JAY, timestamp: '00:35', text: "That's concerning. Could be the new indexing pipeline. Mike, can you dig into it today?" },
        { speakerEmail: SARAH, timestamp: '00:42', text: "Also, I've been chatting with Rachel about the customer dashboard redesign. She has great insights from support tickets." },
        { speakerEmail: MIKE, timestamp: '00:50', text: "Good, we should pull her into the next design review. Let's sync on the performance issue after this." },
      ],
      socialFeedItems: [
        { attendeeEmail: MIKE, text: 'Just started bouldering at the local climbing gym', confidence: 0.88, category: 'Fitness', date: 'Dec 22' },
        { attendeeEmail: SARAH, text: 'Finished an online calligraphy course', confidence: 0.85, category: 'Hobbies', date: 'Dec 20' },
      ],
    },
    {
      id: 'demo-prev-design-review',
      title: 'Design Review: Dashboard Redesign',
      forEventId: 'demo-design-review',
      daysAgo: 5,
      attendeeEmails: [SARAH, RACHEL],
      overview: 'Reviewed new dashboard wireframes and customer feedback patterns.',
      briefSummary: 'Dashboard wireframes reviewed. Rachel surfaced top 5 support pain points. Onboarding flow needs simplification. User testing planned for next sprint.',
      actionItems: [
        { title: 'Simplify onboarding flow to 3 steps', assignedToEmail: SARAH, priority: 'High' },
        { title: 'Schedule 5 user testing sessions', assignedToEmail: RACHEL, priority: 'Medium' },
        { title: 'Review competitive dashboard analysis', assignedToEmail: JESSICA },
        { title: 'Fix mobile action card spacing', assignedToEmail: SARAH, priority: 'Medium' },
        { title: 'Create before/after mockups for board', assignedToEmail: SARAH, priority: 'Low' },
      ],
      transcript: [
        { speakerEmail: JESSICA, timestamp: '00:01', text: "Thanks for pulling this together, Sarah. Rachel, glad you could join. Your support insights are invaluable." },
        { speakerEmail: SARAH, timestamp: '00:08', text: "I have the wireframes ready. The main change is consolidating the three dashboard tabs into a single unified view." },
        { speakerEmail: RACHEL, timestamp: '00:18', text: "That addresses our top support ticket. Users constantly get lost switching between tabs." },
        { speakerEmail: JESSICA, timestamp: '00:28', text: "What about the onboarding flow? That's been our biggest drop-off point." },
        { speakerEmail: SARAH, timestamp: '00:35', text: "I've simplified it from 7 steps to 3. The key insight was that users just want to see value fast." },
        { speakerEmail: RACHEL, timestamp: '00:45', text: "Love it. We should test this with real users before committing. I can recruit from our beta group." },
        { speakerEmail: JESSICA, timestamp: '00:55', text: "Let's plan user testing for next sprint. Sarah, how does the new flow look on mobile?" },
        { speakerEmail: SARAH, timestamp: '01:05', text: "Mobile is responsive but needs some tweaks. The action cards are too cramped on smaller screens." },
      ],
      socialFeedItems: [
        { attendeeEmail: RACHEL, text: 'Just got back from a wine tasting trip in Napa', confidence: 0.90, category: 'Travel', date: 'Dec 21' },
        { attendeeEmail: SARAH, text: 'Adopted a rescue dog named Pixel', confidence: 0.92, category: 'Personal', date: 'Dec 18' },
      ],
    },
    {
      id: 'demo-prev-sales-pipeline',
      title: 'Sales Pipeline Review',
      forEventId: 'demo-sales-review',
      daysAgo: 4,
      attendeeEmails: [DAVID, KATE],
      overview: 'Reviewed Q1 pipeline health and enterprise deal progress.',
      briefSummary: 'Pipeline at 2.8x coverage for Q1 target. Enterprise deal with Meridian stuck on security review. Two mid-market deals closing this week.',
      actionItems: [
        { title: 'Send Meridian security questionnaire responses', assignedToEmail: DAVID, priority: 'High', daysOverdue: 3 },
        { title: 'Prepare case study for Meridian pitch', assignedToEmail: KATE, priority: 'High' },
        { title: 'Follow up on mid-market demo requests', assignedToEmail: DAVID },
        { title: 'Update CRM deal stages for Q1 pipeline', assignedToEmail: DAVID, priority: 'Medium', daysOverdue: 1 },
        { title: 'Draft Meridian enterprise pricing proposal', assignedToEmail: JESSICA, priority: 'High' },
      ],
      transcript: [
        { speakerEmail: DAVID, timestamp: '00:01', text: "Hey team. Pipeline update: we're at 2.8x coverage for Q1 target, which is healthy." },
        { speakerEmail: KATE, timestamp: '00:10', text: "Good. What's the status on the Meridian enterprise deal?" },
        { speakerEmail: DAVID, timestamp: '00:18', text: "It's stuck on their security review. They sent over a questionnaire last week that I need to get back to them on." },
        { speakerEmail: JESSICA, timestamp: '00:28', text: "That's our biggest deal this quarter. David, let's prioritize that response." },
        { speakerEmail: DAVID, timestamp: '00:35', text: "Agreed. I also have two mid-market deals that should close this week. Both are in contract review." },
        { speakerEmail: KATE, timestamp: '00:45', text: "Nice. For Meridian, would a customer case study help? I can pull one together from our success stories." },
        { speakerEmail: JESSICA, timestamp: '00:55', text: "Great idea. David, what else are they concerned about besides security?" },
        { speakerEmail: DAVID, timestamp: '01:05', text: "Integration timeline and dedicated support. Rachel might be able to help with the support plan." },
      ],
      socialFeedItems: [
        { attendeeEmail: DAVID, text: 'Coaching his son\'s little league team this spring', confidence: 0.87, category: 'Family', date: 'Dec 22' },
      ],
    },
    {
      id: 'demo-prev-cs-checkin',
      title: 'Customer Success Check-in',
      forEventId: 'demo-cs-checkin',
      daysAgo: 6,
      attendeeEmails: [RACHEL, DAVID, MIKE],
      overview: 'Reviewed customer health scores and escalation patterns.',
      briefSummary: 'Three at-risk accounts identified. API latency complaints from two enterprise clients. Mike to investigate infrastructure bottleneck.',
      actionItems: [
        { title: 'Investigate API latency for enterprise tier', assignedToEmail: MIKE, priority: 'High' },
        { title: 'Draft retention playbook for at-risk accounts', assignedToEmail: RACHEL, priority: 'High', daysOverdue: 1 },
        { title: 'Schedule QBR with top 3 enterprise accounts', assignedToEmail: DAVID, priority: 'Medium' },
        { title: 'Re-engage champion replacement at risk account', assignedToEmail: RACHEL, priority: 'High', daysOverdue: 2 },
        { title: 'Set up customer health score dashboard', assignedToEmail: RACHEL, priority: 'Medium' },
      ],
      transcript: [
        { speakerEmail: RACHEL, timestamp: '00:01', text: "Thanks for joining. I flagged three at-risk accounts this week. Two are complaining about API latency." },
        { speakerEmail: MIKE, timestamp: '00:10', text: "That might be related to the performance regression I found. Which endpoints are they hitting?" },
        { speakerEmail: RACHEL, timestamp: '00:18', text: "Mostly the reporting API. They're seeing 3-4 second response times during peak hours." },
        { speakerEmail: DAVID, timestamp: '00:28', text: "Those are both accounts I'm trying to upsell. If we lose them, that's 200k in pipeline gone." },
        { speakerEmail: MIKE, timestamp: '00:35', text: "I'll prioritize this. It's likely the same issue we discussed in standup. Let me check the infrastructure." },
        { speakerEmail: RACHEL, timestamp: '00:45', text: "The third at-risk account is a retention issue. They haven't logged in for 3 weeks." },
        { speakerEmail: DAVID, timestamp: '00:55', text: "I know that account. The champion left the company. We need to re-engage with their replacement." },
        { speakerEmail: RACHEL, timestamp: '01:05', text: "I'll draft a retention playbook. Mike, can we get the latency fix in this sprint?" },
      ],
      socialFeedItems: [
        { attendeeEmail: RACHEL, text: 'Started a book club with friends', confidence: 0.86, category: 'Hobbies', date: 'Dec 19' },
        { attendeeEmail: MIKE, text: 'Building a mechanical keyboard from scratch', confidence: 0.89, category: 'Hobbies', date: 'Dec 21' },
      ],
    },
    {
      id: 'demo-prev-all-hands',
      title: 'Weekly All Hands',
      forEventId: 'demo-all-hands',
      daysAgo: 2,
      attendeeEmails: [JAY, KATE, MIKE, SARAH, DAVID, RACHEL],
      overview: 'Company-wide update on Q1 progress, product launches, and team wins.',
      briefSummary: 'Q1 revenue at 85% of target. Product launch on track for March 15. Two new enterprise logos closed. Team growing with 3 open roles.',
      actionItems: [
        { title: 'Finalize product launch announcement copy', assignedToEmail: JESSICA, priority: 'High' },
        { title: 'Coordinate launch day logistics', assignedToEmail: KATE, priority: 'High' },
        { title: 'Prep support team for launch volume', assignedToEmail: RACHEL, priority: 'Medium' },
        { title: 'Update help docs for new features', assignedToEmail: RACHEL, priority: 'Medium' },
        { title: 'Post 3 open roles on job boards', assignedToEmail: KATE, priority: 'Low' },
      ],
      transcript: [
        { speakerEmail: JESSICA, timestamp: '00:01', text: "Good morning everyone! Quick update. We're at 85% of our Q1 revenue target with 5 weeks to go." },
        { speakerEmail: DAVID, timestamp: '00:10', text: "Sales update: we closed two new enterprise logos this month. The Meridian deal is progressing well." },
        { speakerEmail: MIKE, timestamp: '00:20', text: "Engineering is on track for the March 15 product launch. API v2 is in final testing." },
        { speakerEmail: SARAH, timestamp: '00:28', text: "Design has the new dashboard ready for launch. We're running user testing this week." },
        { speakerEmail: RACHEL, timestamp: '00:35', text: "Support is prepping for launch volume. We've updated all help docs and trained the team." },
        { speakerEmail: KATE, timestamp: '00:42', text: "Marketing has the launch campaign ready to go. We need to finalize the announcement copy this week." },
        { speakerEmail: JAY, timestamp: '00:50', text: "Great momentum. Let's make sure launch day logistics are tight. Kate, can you coordinate?" },
        { speakerEmail: JESSICA, timestamp: '01:00', text: "Amazing work team. Let's keep this energy going. Any blockers anyone needs help with?" },
      ],
      socialFeedItems: [],
    },
  ],
};
